import { isDefined } from '@sgme/fp';
import { useContext } from 'react';

import { useTrackEvent } from '@/App/Blotter/useTrackEvent.ts';
import {
  callOrderContext,
  useCallOrderTileId,
  useValidateCallOrder,
} from '@/App/tiles/orders/CallOrder/CallOrderCreationTile/CallOrderContext.tsx';
import { OrderRow } from '@/App/tiles/orders/components/OrderRow.tsx';
import { SgNumberInput } from '@/components/sg-design-system/components/input/SgNumberInput';
import { OmsFieldErrorWrapper } from '@/components/ui/OmsFieldErrorWrapper.tsx';
import { useAppSelector } from '@/store/hooks.ts';
import { selectCallOrderErrors, selectTradeIdFromCallOrderTile } from '@/store/orders/call/selectors.ts';
import { selectOrderEntryByIdFromBlotter } from '@/store/orders/stopLoss/selectors.ts';

type Props = {
  limitPrice: number | undefined;
  setLimitPrice: (value: number | undefined) => void;
};

export const CallOrderEditingLimitPrice = ({ limitPrice, setLimitPrice }: Props) => {
  const { setIsModifyingField } = useContext(callOrderContext);
  const tileId = useCallOrderTileId();

  const track = useTrackEvent();

  const tradeId = useAppSelector((state) => selectTradeIdFromCallOrderTile(state, tileId));
  const orderFromBlotter = useAppSelector((state) =>
    tradeId ? selectOrderEntryByIdFromBlotter(state, tradeId) : undefined,
  );

  const errors = useAppSelector((state) => selectCallOrderErrors(state, tileId));

  const limitPriceError = errors?.limitPrice;

  const validateCallOrder = useValidateCallOrder();

  const handleLimitPriceChange = async (value: number | undefined) => {
    track('Order action', 'Limit price changed');
    if (isDefined(orderFromBlotter)) {
      const response = await validateCallOrder({
        limitPrice: value,
      });

      if (response && 'data' in response && response.data?.validationResponseDetailContent.length === 0) {
        setLimitPrice(value);
      }
    }
  };

  return (
    <OrderRow labelId="order.fields.limitPrice.label">
      <OmsFieldErrorWrapper errors={limitPriceError}>
        <SgNumberInput
          valuePosition="start"
          size="sm"
          maxDecimals={9}
          value={
            limitPrice ??
            (isDefined(orderFromBlotter?.limitPrice) ? (orderFromBlotter?.limitPrice as number) : undefined)
          }
          data-e2e="limit-price"
          onFocus={() => setIsModifyingField(true)}
          onBlur={() => setIsModifyingField(false)}
          onChange={handleLimitPriceChange}
          selectAllOnFocus
          state={isDefined(limitPriceError) ? 'invalid' : undefined}
        />
      </OmsFieldErrorWrapper>
    </OrderRow>
  );
};
